import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import { FieldCollectionAttribute } from 'features/views/LayoutEditor/widgets/common/FieldCollection/types'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

// minWidth: columnCount
const CONTAINER_COLUMN_COUNTS: Record<string, number> = {
    '0': 1,
    '421': 2,
    '801': 3,
    '1201': 4,
}
const CONTAINER_WIDTHS = [1201, 801, 421, 0]

type UseFieldCollectionStateProps = {
    fields: FieldCollectionAttribute[]
}

export function useFieldCollectionState({ fields }: UseFieldCollectionStateProps) {
    const { isLoadingSlow } = useRecordManagerContext()
    const { isEditing } = useLayoutEditorContext()

    const fieldsLength = fields.length

    const [columnCount, setColumnCount] = useState(1)
    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null)
    useLayoutEffect(() => {
        const wrapper = wrapperRef
        if (!wrapper) return

        const calculateColumnCount = () => {
            const width = wrapper.clientWidth

            const containerWidth = CONTAINER_WIDTHS.find((key) => {
                return width >= key
            })
            if (typeof containerWidth !== 'number') return

            // Just in case the fields are less than the container width.
            let columnCount = CONTAINER_COLUMN_COUNTS[containerWidth.toString()]
            if (fieldsLength < columnCount) {
                columnCount = fieldsLength
            }
            setColumnCount(columnCount)
        }

        calculateColumnCount()

        const resizeObserver = new ResizeObserver(calculateColumnCount)
        resizeObserver.observe(wrapper)

        return () => {
            resizeObserver.disconnect()
        }
    }, [wrapperRef, fieldsLength])

    const fieldsByColumn = useMemo(() => {
        const columns: FieldCollectionAttribute[][] = Array.from({ length: columnCount }, () => [])

        for (let i = 0; i < fields.length; i++) {
            const field = fields[i]

            const targetColumnIdx = i % columnCount
            columns[targetColumnIdx]?.push(field)
        }

        return columns
    }, [fields, columnCount])
    const fieldsByColumnMemo = useDeepEqualsMemoValue(fieldsByColumn)

    const [editingFieldsApiNames, setEditingFieldsApiNames] = useState<string[]>([])

    const isEditingRef = useRef(isEditing)
    isEditingRef.current = isEditing

    const toggleEditingField = useCallback((fieldApiName: string, enabled: boolean) => {
        if (isEditingRef.current) return

        setEditingFieldsApiNames((prev) => {
            if (enabled) {
                return [...prev, fieldApiName]
            }

            return prev.filter((name) => name !== fieldApiName)
        })
    }, [])

    return {
        fieldsByColumn: fieldsByColumnMemo,
        isLoadingSlow,
        wrapperRef: setWrapperRef,
        toggleEditingField,
        editingFieldsApiNames,
        isEditingLayout: isEditing,
    }
}
